<!--
  EditorArtworkPicker -- canonical product-editor artwork picker modal.

  Card: https://trello.com/c/hYlwyKYT

  Two callsites:
    - VariantCard "Replace" button (hosted in DecorationInspector). User
      swaps the artwork bound to a server or pending variant.
    - AddPlacementModal "Artwork (optional)" field. User can pick or
      upload artwork to attach to the newly-staged decoration's default
      variant.

  Surface:
    - Lists tenant-scoped Artwork rows from the artworks Pinia store.
      Picker grid; click an artwork to select it.
    - "Upload new" button toggles an inline file-picker form. The upload
      reuses ``artworksStore.uploadArtwork`` (the same action the
      decoration-library UploadArtworkModal calls) so the created
      Artwork lands in the same tenant repository.
    - Emits 'picked' with the resolved {artworkFileId, artworkFilename,
      inkColorHex} so the parent can call productEditor.bindVariantArtwork
      OR stage on a fresh variant.
    - Emits 'close' on cancel / scrim / Esc.

  Editor-local design choice: kept smaller than the decoration-library
  UploadArtworkModal -- no repository picker (defaults to the tenant's
  first repository, or whatever the artworks store hydrated), no per-file
  ink color edit beyond what the source row already carries. That avoids
  introducing a second source of truth for repository management inside
  the editor surface.

  Identifier passed downstream:
    The variant.artwork_file_id field is a free-form string. We surface
    the Artwork row id as the canonical identifier (string-coerced) --
    matches getArtworkForVariant's string-eq match path in the artworks
    store. When the formal Variant.artwork_id FK lands in a later Phase
    F PR, this is the value that will key the relationship.
-->
<template>
    <div
        v-if="modelValue"
        class="editor-artwork-picker-backdrop"
        data-testid="editor-artwork-picker"
        @click.self="onCancel"
    >
        <div class="editor-artwork-picker" role="dialog" aria-modal="true">
            <header class="eap-header">
                <h3 class="eap-title">{{ title }}</h3>
                <button
                    type="button"
                    class="eap-close"
                    data-testid="editor-artwork-picker-close"
                    aria-label="Close"
                    @click="onCancel"
                >
                    &times;
                </button>
            </header>

            <div class="eap-body">
                <div class="eap-toolbar">
                    <button
                        type="button"
                        class="eap-tab"
                        :class="{ 'is-active': mode === 'pick' }"
                        data-testid="editor-artwork-picker-mode-pick"
                        @click="setMode('pick')"
                    >
                        Pick from library
                    </button>
                    <button
                        type="button"
                        class="eap-tab"
                        :class="{ 'is-active': mode === 'upload' }"
                        data-testid="editor-artwork-picker-mode-upload"
                        @click="setMode('upload')"
                    >
                        Upload new
                    </button>
                </div>

                <!-- PICK mode -->
                <div
                    v-if="mode === 'pick'"
                    class="eap-pane"
                    data-testid="editor-artwork-picker-pick-pane"
                >
                    <!--
                      ADR 0015: the library is filtered by default to the
                      store's client; "All decorations" lifts the filter
                      for the licensed-logo / shared-mark cases (and is
                      the entry point for ADR 0017 cross-client reuse).
                      Only offered when there IS a client to filter by.
                    -->
                    <label
                        v-if="showAllDecorationsToggle"
                        class="eap-all-toggle"
                        data-testid="editor-artwork-picker-all-decorations-label"
                    >
                        <input
                            v-model="showAllDecorations"
                            type="checkbox"
                            data-testid="editor-artwork-picker-all-decorations"
                            @change="onToggleAllDecorations"
                        />
                        <span>All decorations</span>
                    </label>

                    <p
                        v-if="artworks.length === 0"
                        class="eap-empty"
                        data-testid="editor-artwork-picker-empty"
                    >
                        No artwork available yet. Upload a new one.
                    </p>
                    <ul v-else class="eap-grid">
                        <li
                            v-for="a in artworks"
                            :key="a.id"
                            class="eap-grid-item"
                            :data-testid="`editor-artwork-picker-row-${a.id}`"
                        >
                            <button
                                type="button"
                                class="eap-grid-btn"
                                :data-testid="`editor-artwork-picker-pick-${a.id}`"
                                @click="onPick(a)"
                            >
                                <span class="eap-grid-thumb">
                                    <DecorationArtThumb
                                        v-if="thumbSrc(a)"
                                        :src="thumbSrc(a)"
                                        :ink-hex="a.dominant_ink_hex"
                                        :ink-mixed="a.dominant_ink_mixed === true"
                                        :alt="displayName(a)"
                                        loading="lazy"
                                        class="eap-grid-img"
                                        :data-testid="`editor-artwork-picker-img-${a.id}`"
                                    />
                                    <span
                                        v-else
                                        class="eap-grid-placeholder"
                                        :data-testid="`editor-artwork-picker-placeholder-${a.id}`"
                                    >
                                        {{ placeholderLetter(a) }}
                                    </span>
                                </span>
                                <span
                                    class="eap-grid-name"
                                    :data-testid="`editor-artwork-picker-name-${a.id}`"
                                >{{ displayName(a) }}</span>
                                <span
                                    v-if="fileType(a)"
                                    class="eap-grid-type"
                                    :data-testid="`editor-artwork-picker-type-${a.id}`"
                                >{{ fileType(a) }}</span>
                                <span
                                    class="eap-grid-meta"
                                    :data-testid="`editor-artwork-picker-meta-${a.id}`"
                                >
                                    v{{ a.version ?? 1 }}
                                </span>
                            </button>
                        </li>
                    </ul>
                </div>

                <!-- UPLOAD mode -->
                <form
                    v-else
                    class="eap-pane"
                    data-testid="editor-artwork-picker-upload-pane"
                    @submit.prevent="onUploadSubmit"
                >
                    <label class="eap-label" for="eap-file">Artwork file</label>
                    <input
                        id="eap-file"
                        type="file"
                        :accept="acceptTypes"
                        data-testid="editor-artwork-picker-file"
                        @change="onFileChange"
                    />
                    <p
                        v-if="fileError"
                        class="eap-error"
                        data-testid="editor-artwork-picker-file-error"
                    >
                        {{ fileError }}
                    </p>

                    <!--
                        Bug Intake #332 (GH #4292): a .dst/.emb a client or a
                        team member already approved. Ticking it pre-selects
                        this store's client (or, for a team member, the
                        signed-in member). Not offered on the SuperAdmin
                        distributor-scoped surface (no store, no client).
                    -->
                    <PriorApprovalAttestation
                        v-if="!isDistributorScoped"
                        v-model="priorApproval"
                        :file-name="file ? file.name : ''"
                        :default-client-id="clientId"
                        :distributor-id="uploadDistributorId"
                        :disabled="uploading || uploaded"
                    />

                    <label class="eap-label" for="eap-repo">Repository</label>
                    <select
                        id="eap-repo"
                        v-model="repositoryId"
                        data-testid="editor-artwork-picker-repo"
                    >
                        <option :value="null" disabled>Choose a repository...</option>
                        <option
                            v-for="r in repositories"
                            :key="r.id"
                            :value="r.id"
                        >
                            {{ r.name }}
                        </option>
                    </select>
                    <p
                        v-if="repoError"
                        class="eap-error"
                        data-testid="editor-artwork-picker-repo-error"
                    >
                        {{ repoError }}
                    </p>

                    <DuplicateArtworkNotice
                        v-if="duplicates.length"
                        :duplicates="duplicates"
                        :busy="uploading"
                        @use-existing="onUseExisting"
                        @upload-anyway="onUploadSubmit({ allowDuplicate: true })"
                    />

                    <p
                        v-if="uploadError"
                        class="eap-error"
                        data-testid="editor-artwork-picker-upload-error"
                    >
                        {{ uploadError }}
                    </p>

                    <div class="eap-footer">
                        <button
                            type="button"
                            class="eap-btn"
                            data-testid="editor-artwork-picker-cancel"
                            :disabled="uploading"
                            @click="onCancel"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="eap-btn eap-btn--primary"
                            data-testid="editor-artwork-picker-upload-submit"
                            :disabled="!canUpload"
                            @click="onUploadSubmit"
                        >
                            {{ uploading ? 'Uploading...' : 'Upload and use' }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * EditorArtworkPicker -- pick or upload an Artwork from inside the
 * product editor surface. See top-of-file comment for the full contract.
 */
import api from '@/services/api'
import { useArtworksStore } from '@/stores/artworks'
import { artworkFileTypeLabel } from '@/utils/artworkDisplay'
import DecorationArtThumb from '@/components/decoration/DecorationArtThumb.vue'
import PriorApprovalAttestation from '@/components/decoration/PriorApprovalAttestation.vue'
import {
    clearedPriorApproval,
    isPriorApprovalComplete,
    priorApprovalUploadArgs,
} from '@/utils/priorApproval'
import DuplicateArtworkNotice from '@/components/decoration/DuplicateArtworkNotice.vue'
import { artworkImageSrc } from '@/utils/artworkPreviewState'
import {
    formatArtworkName,
    artworkPayloadName,
} from '@/utils/formatArtworkName'
import {
    ARTWORK_UPLOAD_ACCEPT,
    validateArtworkFile,
} from '@/utils/artworkUploadContract'

// POST /artworks/preview_states accepts at most this many ids per request
// (api/src/endpoints/artworks.py _PREVIEW_STATES_MAX_IDS).
const PREVIEW_STATES_MAX_IDS = 500

export default {
    name: 'EditorArtworkPicker',

    components: {
        DecorationArtThumb,
        DuplicateArtworkNotice,
        PriorApprovalAttestation,
    },

    props: {
        modelValue: { type: Boolean, default: false },
        title: { type: String, default: 'Choose artwork' },
        // Optional explicit distributor scope. When set (e.g. the
        // SuperAdmin-gated Artifi prototype, where the caller has no tenant
        // of its own), the picker loads THAT distributor's artworks +
        // repositories via the SAFRS distributor_id filter and holds them in
        // local state -- it does NOT touch the tenant-scoped artworks store
        // (store.fetch()/`/mine` return nothing / all-tenants for an SA).
        // When null (the default / existing callsites), behavior is
        // unchanged: read from the caller-tenant-scoped store.
        distributorId: {
            type: [String, Number],
            default: null,
        },
        /**
         * ADR 0015 ("Library scoping in the product editor"): the store's
         * client. When set, the picker DEFAULTS to showing only that
         * client's decorations -- "Samsung's store sees Samsung's approved
         * decorations" -- and offers an "All decorations" toggle to lift
         * the filter.
         *
         * The value originates as ``campaign_products.store_client_id``
         * (the owning store's client), because the product-editor route
         * carries only ``:campaignProductId``.
         *
         * NULL is a legitimate state, not a missing prop:
         * ``stores.client_id`` is nullable ("existing campaigns predate
         * the Client tenant"). With no client there is nothing to narrow
         * to, so the picker behaves exactly as it did before ADR 0015 --
         * tenant-scoped store fetch, no toggle offered.
         */
        clientId: {
            type: [String, Number],
            default: null,
        },
        /**
         * Artwork ids ALREADY applied to this product. They stay visible
         * even when the client filter would exclude them: a Builder who
         * can see a decoration applied to the product must be able to
         * find it in the picker, or the editor reads as broken.
         */
        inUseArtworkIds: {
            type: Array,
            default: () => [],
        },
    },

    emits: ['update:modelValue', 'picked', 'close'],

    data() {
        return {
            mode: 'pick',
            file: null,
            repositoryId: null,
            fileError: null,
            repoError: null,
            uploadError: null,
            uploading: false,
            // Bug Intake #332: { attested, attester, clientId, memberId }
            // from the "already approved by [client|team member]" control.
            priorApproval: clearedPriorApproval(),
            // True once the file is uploaded but its prior approval was
            // refused: a second submit would upload a duplicate.
            uploaded: false,
            // Bug Intake #310: byte-identical artwork the tenant already
            // has, held for the user's choice.
            duplicates: [],
            // In-flight guard so rapid open/close cycles do not trigger
            // duplicate hydration round trips. Resets on unmount.
            _fetchInFlight: false,
            // Distributor-scoped data (only used when distributorId is set).
            _scopedArtworks: [],
            _scopedRepositories: [],
            // Which distributor the scoped data was hydrated for (dedup key),
            // and a flag to re-drive hydration when a distributor change
            // superseded an in-flight fetch.
            _hydratedFor: null,
            _needsRehydrate: false,
            // ADR 0015: false = filtered to this store's client (the
            // DEFAULT), true = "All decorations" (filter lifted).
            showAllDecorations: false,
            // Client-scoped rows, held locally so the shared tenant-wide
            // artworks store is never narrowed under other consumers.
            _clientArtworks: [],
            _clientHydratedFor: null,
            // Stitch-file preview sidecars ({state, preview_url}) keyed by
            // artwork id, from POST /artworks/preview_states. The SAFRS
            // rows this picker lists cannot carry them (see
            // _hydratePreviews), and the shared tenant-wide store rows are
            // never mutated, so they are held here and joined at render.
            previewByArtworkId: {},
            // The id set the last preview_states request covered.
            previewsFetchedFor: null,
            // Bumped by every request and by open/close; a response whose
            // token is no longer current (the dialog closed, or the scope
            // changed and re-asked) is ignored.
            previewsRequestToken: 0,
        }
    },

    computed: {
        acceptTypes() {
            return ARTWORK_UPLOAD_ACCEPT
        },
        // The chosen repository's distributor: the prior-approval picker
        // offers only that tenant's clients (#4292).
        uploadDistributorId() {
            const repo = this.repositories.find(
                (r) => String(r.id) === String(this.repositoryId),
            )
            return repo?.distributor_id ?? null
        },
        canUpload() {
            return (
                !this.uploading &&
                !this.uploaded &&
                isPriorApprovalComplete(this.priorApproval)
            )
        },
        // True when the picker is scoped to an explicit distributor rather
        // than the caller's own tenant.
        isDistributorScoped() {
            return (
                this.distributorId != null &&
                String(this.distributorId) !== ''
            )
        },
        // ADR 0015: true when this picker has a store client to default
        // to. An explicit distributorId (the SuperAdmin Artifi prototype)
        // wins -- that surface has no store, so no client, and its
        // existing behaviour is unchanged.
        isClientScoped() {
            return (
                !this.isDistributorScoped &&
                this.clientId != null &&
                String(this.clientId) !== ''
            )
        },
        // The "All decorations" toggle is only meaningful when there IS a
        // filter to lift.
        showAllDecorationsToggle() {
            return this.isClientScoped
        },
        artworks() {
            if (this.isDistributorScoped) return this._scopedArtworks
            // A client-scoped picker owns its list in BOTH toggle states.
            // Falling back to the shared tenant store when the filter is
            // lifted would make "All decorations" show whatever that
            // store happened to hold -- possibly stale, possibly empty on
            // a direct-mount surface -- instead of a real refetch.
            if (this.isClientScoped) return this._clientArtworks
            const store = useArtworksStore()
            return Array.isArray(store.artworks) ? store.artworks : []
        },
        // Changes whenever the listed rows change (open, scope toggle,
        // re-scope, store hydration), which is when previews are needed.
        // Distinct ids: a repeated row must not be sent twice (and count
        // twice toward the endpoint's 500-id cap).
        listedArtworkIdsKey() {
            const ids = this.artworks
                .map((a) => (a?.id == null ? '' : String(a.id)))
                .filter((id) => id !== '')
            return [...new Set(ids)].sort().join(',')
        },
        repositories() {
            if (this.isDistributorScoped) return this._scopedRepositories
            const store = useArtworksStore()
            return Array.isArray(store.repositories) ? store.repositories : []
        },
    },

    watch: {
        modelValue(open) {
            // Any open/close supersedes an in-flight preview request.
            this.previewsRequestToken += 1
            if (open) {
                this._reset()
                this._ensureHydrated()
                // A re-open re-asks: a preview that was still rendering
                // last time may be ready now.
                this.previewsFetchedFor = null
                this._hydratePreviews()
            }
        },
        listedArtworkIdsKey() {
            this._hydratePreviews()
        },
        // Re-scope when the caller changes the distributor while open (e.g.
        // the SA picks a different distributor in the Artifi prototype).
        distributorId() {
            if (this.modelValue) {
                this._scopedArtworks = []
                this._scopedRepositories = []
                this._hydratedFor = null
                this._reset()
                this._ensureHydrated()
            }
        },
        // Re-scope when the editor moves to a product in a different
        // store (and therefore a different client) while the picker is
        // open. Mirrors the distributorId watcher above.
        clientId() {
            if (this.modelValue) {
                this._clientArtworks = []
                this._clientHydratedFor = null
                this._reset()
                this._ensureHydrated()
            }
        },
    },

    // Seed the repository default when the modal mounts already-open
    // (modelValue=true at mount). The watcher only fires on changes, so
    // without this the upload form starts with repositoryId=null even
    // though the store has repositories hydrated.
    //
    // Also self-hydrate the artworks store on mount. The parent
    // (ProductEditor.vue) calls artworks.fetch() but the modal can
    // render before that resolves -- and direct-mount surfaces (eg
    // AddPlacementModal opened from a deep link) bypass the parent
    // fetch entirely. Idempotent: skipped when both collections are
    // already populated.
    mounted() {
        if (this.modelValue) this._reset()
        this._ensureHydrated()
        this._hydratePreviews()
    },

    methods: {
        _ensureHydrated() {
            if (this.isDistributorScoped) {
                this._ensureHydratedScoped()
                return
            }
            if (this.isClientScoped) {
                this._ensureHydratedForClient()
                return
            }
            const store = useArtworksStore()
            const haveArtworks =
                Array.isArray(store.artworks) && store.artworks.length > 0
            const haveRepos =
                Array.isArray(store.repositories) &&
                store.repositories.length > 0
            if (haveArtworks && haveRepos) return
            if (this._fetchInFlight) return
            if (typeof store.fetch !== 'function') return
            this._fetchInFlight = true
            // Re-seed the repository default once the fetch lands so
            // the upload form's select is populated for the common
            // single-repository case even when this modal triggered
            // the initial hydration.
            Promise.resolve(store.fetch())
                .then(() => {
                    if (this.repositoryId == null) {
                        const repos = this.repositories
                        this.repositoryId =
                            repos.length > 0 ? repos[0].id : null
                    }
                })
                .catch(() => {
                    // The store's fetch() already records error.value;
                    // the picker just falls back to the empty state.
                })
                .finally(() => {
                    this._fetchInFlight = false
                })
        },

        // ADR 0015 client-scoped hydration: load only this store's
        // client's artwork (plus anything already applied to the product).
        //
        // The NARROWING IS THE SERVER'S JOB -- ``Artwork.jsonapi_filter``
        // gives ``filter[client_id]`` "this client OR unassociated"
        // semantics against the ``artwork_clients`` many-to-many join
        // (GH #3111). Associations accrue only as designs are chosen, so
        // a bare match would hide most of a real library. Doing the
        // filtering client-side instead would mean shipping the whole
        // library to the browser, which leaks the very cross-client names
        // ADR 0017 says clients must not learn about each other.
        //
        // Results are held in LOCAL state, never written into the shared
        // artworks store: that store is tenant-wide and other surfaces
        // (the Decoration Library list) read it.
        _ensureHydratedForClient() {
            if (this._fetchInFlight) return
            const all = this.showAllDecorations === true
            const key = `${this.clientId}|${all}|${
                this._inUseIds().join(',')
            }`
            if (this._clientHydratedFor === key) return

            this._fetchInFlight = true
            const cid = this.clientId
            const params = { 'page[limit]': 250 }
            if (!all) {
                // ADR 0015 default. Omitted entirely when "All
                // decorations" is on -- that is what lifts the filter,
                // and it is the path ADR 0017 cross-client reuse needs.
                params['filter[client_id]'] = cid
            }
            const inUse = this._inUseIds()
            const requests = [api.get('/artworks/', { params })]
            if (inUse.length && !all) {
                // A SECOND request rather than one union: SAFRS applies
                // sibling filter[...] params conjunctively, so
                // filter[client_id] AND filter[id] would return the
                // intersection (empty), not the union we need. Both
                // responses stay inside the caller's tenant -- the
                // distributor predicate is unconditional on the server.
                requests.push(
                    api.get('/artworks/', {
                        params: {
                            'filter[id]': inUse.join(','),
                            'page[limit]': 250,
                        },
                    }),
                )
            }

            Promise.all(requests)
                .then((responses) => {
                    if (String(cid) !== String(this.clientId)) {
                        this._needsRehydrate = true
                        return
                    }
                    const byId = new Map()
                    responses.forEach((res) => {
                        ;(res?.data?.data || []).forEach((row) => {
                            const art = this._safrsRowToArtwork(row)
                            if (!byId.has(String(art.id))) {
                                byId.set(String(art.id), art)
                            }
                        })
                    })
                    this._clientArtworks = Array.from(byId.values())
                    this._clientHydratedFor = key
                })
                .catch(() => {
                    // Fall back to the empty state; the pane shows "No
                    // artwork available yet."
                    this._clientArtworks = []
                })
                .finally(() => {
                    this._fetchInFlight = false
                    if (this._needsRehydrate) {
                        this._needsRehydrate = false
                        this._ensureHydrated()
                    }
                })
        },

        // Stitch-file previews for the listed rows. A .dst/.emb row's
        // ``image_url`` is the raw stitch file (not an image) and its
        // ``thumbnail_url`` is NULL; the picture to show is its rendered
        // ``artwork_preview`` sidecar. The SAFRS ``/artworks/`` payload
        // deliberately cannot carry that sidecar (its rollup commits the
        // lazy render kick), so -- exactly as the product editor does for
        // its variants (productEditor.js _hydratePreviewStates) -- ask
        // ``POST /artworks/preview_states`` for the listed ids. Only rows
        // that HAVE a sidecar come back; raster rows are absent.
        //
        // Best-effort: a failure leaves the map as it was, and a stitch
        // row then shows its letter placeholder (artworkImageSrc never
        // returns the raw stitch file), never a broken image. No polling:
        // a preview still rendering stays a placeholder until the listed
        // rows change or the dialog re-opens.
        _hydratePreviews() {
            if (!this.modelValue) return
            const key = this.listedArtworkIdsKey
            if (!key || key === this.previewsFetchedFor) return
            this.previewsFetchedFor = key
            this.previewsRequestToken += 1
            const token = this.previewsRequestToken
            // The endpoint refuses more than 500 ids per request (400), so
            // a large library is asked in batches rather than not at all.
            const ids = key.split(',')
            const batches = []
            for (let i = 0; i < ids.length; i += PREVIEW_STATES_MAX_IDS) {
                batches.push(ids.slice(i, i + PREVIEW_STATES_MAX_IDS))
            }
            // allSettled, not all: one failed batch must not discard the
            // previews the other batches returned.
            Promise.allSettled(
                batches.map((batch) =>
                    Promise.resolve(
                        api.post('/artworks/preview_states', {
                            artwork_ids: batch,
                        }),
                    ),
                ),
            ).then((results) => {
                // Stale: the dialog closed, or a newer list re-asked.
                if (token !== this.previewsRequestToken) return
                let next = this.previewByArtworkId
                let anyFailed = false
                results.forEach((r) => {
                    if (r.status !== 'fulfilled') {
                        anyFailed = true
                        return
                    }
                    const previews = r.value?.data?.previews
                    if (previews && typeof previews === 'object') {
                        next = { ...next, ...previews }
                    }
                })
                this.previewByArtworkId = next
                // Let the next list change or re-open retry what failed.
                if (anyFailed && this.previewsFetchedFor === key) {
                    this.previewsFetchedFor = null
                }
            })
        },

        // Normalized, de-duplicated in-use artwork ids as strings.
        _inUseIds() {
            const raw = Array.isArray(this.inUseArtworkIds)
                ? this.inUseArtworkIds
                : []
            const out = []
            raw.forEach((v) => {
                if (v == null) return
                const sv = String(v).trim()
                if (sv && !out.includes(sv)) out.push(sv)
            })
            return out
        },

        // ADR 0015 "All decorations": lifting the filter must REFETCH, not
        // just re-render -- the client-filtered response never contained
        // the other clients' rows, so there is nothing local to reveal.
        // This is also the path ADR 0017's cross-client reuse depends on.
        onToggleAllDecorations() {
            this._ensureHydrated()
        },

        // Distributor-scoped hydration: load THIS distributor's artworks +
        // repositories via SAFRS filters (the tenant-scoped store.fetch() /
        // `/artwork_repositories/mine` can't be distributor-parameterized).
        // Holds results in local state so the shared store is never polluted
        // with cross-tenant rows.
        _ensureHydratedScoped() {
            if (this._fetchInFlight) return
            // Dedup on which distributor we've hydrated, NOT array length: a
            // distributor with repos but zero artworks would otherwise
            // re-fetch on every open (empty _scopedArtworks).
            if (String(this._hydratedFor) === String(this.distributorId)) {
                return
            }
            this._fetchInFlight = true
            const did = this.distributorId
            Promise.all([
                api.get('/artworks/', {
                    params: { 'filter[distributor_id]': did },
                }),
                api.get('/artwork_repositories/', {
                    params: { 'filter[distributor_id]': did },
                }),
            ])
                .then(([artRes, repoRes]) => {
                    // If the distributor changed mid-flight, this response is
                    // stale -- drop it AND flag a re-hydrate so the now-current
                    // distributor actually loads (the in-flight guard above
                    // suppressed its fetch; without this the pane would stall
                    // empty until reopened).
                    if (String(did) !== String(this.distributorId)) {
                        this._needsRehydrate = true
                        return
                    }
                    this._scopedArtworks = (artRes.data?.data || []).map((row) =>
                        this._safrsRowToArtwork(row)
                    )
                    this._scopedRepositories = (repoRes.data?.data || []).map(
                        (row) => ({
                            id: row.id,
                            name: row.attributes?.name || `Repository ${row.id}`,
                        })
                    )
                    this._hydratedFor = did
                    if (this.repositoryId == null) {
                        this.repositoryId =
                            this._scopedRepositories.length > 0
                                ? this._scopedRepositories[0].id
                                : null
                    }
                })
                .catch(() => {
                    // Fall back to the empty state; the pane shows "No
                    // artwork available yet."
                })
                .finally(() => {
                    this._fetchInFlight = false
                    // Re-drive for the current distributor if a change
                    // superseded the fetch we just finished.
                    if (this._needsRehydrate) {
                        this._needsRehydrate = false
                        this._ensureHydratedScoped()
                    }
                })
        },

        // Flatten a SAFRS Artwork row {id, attributes:{...}} into the flat
        // shape the grid renders (matches the store's normalized rows).
        _safrsRowToArtwork(row) {
            const a = row.attributes || {}
            return {
                id: row.id,
                filename: a.filename,
                // The CURRENT file's name: the shared tile rule reads its
                // extension to keep a raw stitch/vector file out of <img>.
                original_filename: a.original_filename,
                image_url: a.image_url,
                thumbnail_url: a.thumbnail_url,
                artifi_unique_name: a.artifi_unique_name,
                ink_color_hex: a.ink_color_hex,
                // ADR 0062 sampled ink: the grid's DecorationArtThumb picks
                // its backdrop from these (it was always fed them; the
                // flattening dropped them).
                dominant_ink_hex: a.dominant_ink_hex,
                dominant_ink_mixed: a.dominant_ink_mixed,
                version: a.version,
                // GH #3111: no ``client_id`` here. The column was dropped
                // (the association is many-to-many in ``artwork_clients``
                // now) and nothing in this component ever read the field
                // -- the NARROWING is the server's job (see
                // ``_ensureHydratedForClient`` below). Carrying a field
                // the payload no longer contains would read as a
                // supported client hint that is permanently null.
            }
        },

        _reset() {
            this.mode = 'pick'
            this.file = null
            this.fileError = null
            this.repoError = null
            this.uploadError = null
            this.uploading = false
            this.priorApproval = clearedPriorApproval()
            this.uploaded = false
            this.duplicates = []
            // Default the repository to the first one available so the
            // user does not have to click into the select for the common
            // single-repository case.
            const repos = this.repositories
            this.repositoryId = repos.length > 0 ? repos[0].id : null
        },

        setMode(m) {
            if (this.uploading) return
            this.mode = m
            this.uploadError = null
        },

        // The real file's type for the tile chip (PDF, EMB, ...).
        fileType(a) {
            return artworkFileTypeLabel(a)
        },

        // DISPLAY name (tile label / alt): the shared display formatter.
        displayName(a) {
            return formatArtworkName(a?.filename) || `Artwork #${a?.id ?? '?'}`
        },

        // PERSISTED name emitted as `artworkFilename` in the `picked`
        // payload. Same cut as the display formatter (one core in
        // utils/formatArtworkName) via the payload-safe variant, whose
        // leading-'#' -> raw semantics keep the stored value byte-stable.
        // Do not swap this for formatArtworkName (2026-07-10, #2018 review).
        payloadName(a) {
            const raw = a?.filename
            if (typeof raw !== 'string' || !raw.trim()) {
                return `Artwork #${a?.id ?? '?'}`
            }
            return artworkPayloadName(raw)
        },

        placeholderLetter(a) {
            const name = this.displayName(a)
            if (!name) return 'A'
            return name[0].toUpperCase()
        },

        // The tile image, by the SAME rule the Decoration Library grid
        // uses (utils/artworkPreviewState.js artworkImageSrc): a stitch
        // file's READY rendered preview, else the server thumbnail, else
        // image_url -- never a raw .dst/.emb or EPS/AI/PDF. Null -> the
        // letter placeholder. No ``?v=`` cache-bust: every uploaded file
        // and rendered preview has its own uuid key, so a new version is
        // already a new URL (and the library does not append one).
        thumbSrc(a) {
            if (!a) return null
            const preview = this.previewByArtworkId[String(a.id)]
            return artworkImageSrc(
                preview === undefined ? a : { ...a, preview },
            )
        },

        onPick(a) {
            this.$emit('picked', {
                artworkFileId: String(a.id),
                artworkFilename: this.payloadName(a),
                inkColorHex: a?.ink_color_hex ?? null,
                // Additive: surface the Artifi UniqueName + a thumbnail so
                // callers that need them (e.g. the Artifi Designer prototype
                // preload) don't have to re-fetch the row. May be null when
                // the artwork hasn't been registered with Artifi yet.
                artifiUniqueName: a?.artifi_unique_name ?? null,
                thumbnailUrl: a?.thumbnail_url || a?.image_url || null,
            })
            this.$emit('update:modelValue', false)
            this.$emit('close')
        },

        onFileChange(evt) {
            const f = evt?.target?.files?.[0] ?? null
            this.file = f
            // Immediate feedback on pick; shared contract copy.
            this.fileError = f ? validateArtworkFile(f).error : null
        },

        async onUploadSubmit({ allowDuplicate = false } = {}) {
            if (!this.canUpload) return
            let ok = true
            const fileCheck = validateArtworkFile(this.file)
            this.fileError = fileCheck.error
            if (!fileCheck.ok) ok = false
            if (!this.repositoryId) {
                this.repoError = 'Choose a repository.'
                ok = false
            } else {
                this.repoError = null
            }
            if (!ok) return

            this.uploading = true
            this.uploadError = null
            this.duplicates = []
            try {
                const store = useArtworksStore()
                const created = await store.uploadArtwork({
                    // Bug Intake #310: hold a byte-identical re-upload and
                    // offer the existing artwork instead of forking a copy.
                    checkDuplicates: true,
                    allowDuplicate,
                    file: this.file,
                    filename: this.file.name,
                    repositoryId: this.repositoryId,
                    // Pass the explicit distributor scope when set so a
                    // caller with no tenant (SA) can upload; the store adds
                    // distributor_id to the create and the backend @validates
                    // allows it for a true SA. Null for tenant-scoped callers.
                    // Coerce to Number: the prop arrives as a string from a
                    // <select> v-model, but the create payload should carry a
                    // numeric distributor_id (matches the DB column type).
                    distributorId: this.isDistributorScoped
                        ? Number(this.distributorId)
                        : null,
                    // Bug Intake #332: the client or team member who
                    // already approved this stitch file, when ticked.
                    ...priorApprovalUploadArgs(this.priorApproval),
                })
                this.$emit('picked', {
                    artworkFileId: String(created?.id ?? ''),
                    artworkFilename: this.payloadName(created ?? {
                        filename: this.file.name,
                        id: '',
                    }),
                    inkColorHex: created?.ink_color_hex ?? null,
                    artifiUniqueName: created?.artifi_unique_name ?? null,
                    thumbnailUrl:
                        created?.thumbnail_url || created?.image_url || null,
                })
                this.$emit('update:modelValue', false)
                this.$emit('close')
            } catch (err) {
                if (err && err.kind === 'duplicate') {
                    this.duplicates = err.duplicates
                    return
                }
                // The file uploaded but its prior approval was refused:
                // say so and keep the pane, without offering a re-upload.
                if (err?.uploadedArtwork) this.uploaded = true
                this.uploadError =
                    (err && err.message) || 'Upload failed. Try again.'
            } finally {
                this.uploading = false
            }
        },

        // Bug Intake #310: the user chose the artwork the tenant already
        // has. Same emit as choosing it from the list, so no caller knows
        // the difference.
        onUseExisting(existing) {
            this.onPick({
                id: existing.id,
                filename: existing.name,
                thumbnail_url: existing.thumbnail_url ?? null,
            })
        },

        onCancel() {
            if (this.uploading) return
            this.$emit('update:modelValue', false)
            this.$emit('close')
        },
    },
}
</script>

<style scoped>
.editor-artwork-picker-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.editor-artwork-picker {
    background: var(--tf-bg, #fff);
    border-radius: 8px;
    width: min(560px, 92vw);
    max-height: 92vh;
    overflow-y: auto;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
}

.eap-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.eap-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}

.eap-close {
    background: none;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: rgba(0, 0, 0, 0.6);
}

.eap-body {
    padding: 16px 20px;
}

.eap-toolbar {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
    border-bottom: 1px solid var(--tf-border, #e0e0e0);
}

.eap-tab {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 6px 10px;
    font-size: 13px;
    cursor: pointer;
    color: var(--tf-text-muted, #6b7280);
}

.eap-tab.is-active {
    color: var(--tf-text-primary, #133752);
    border-bottom-color: var(--tf-brand-primary, #119ae9);
    font-weight: 600;
}

.eap-pane {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.eap-grid {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 8px;
    max-height: 320px;
    overflow-y: auto;
}

.eap-grid-item {
    list-style: none;
}

.eap-grid-btn {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 10px 12px;
    border: 1px solid var(--tf-border, #e0e0e0);
    border-radius: 6px;
    background: var(--tf-bg-card, #fff);
    cursor: pointer;
    text-align: left;
}

.eap-grid-btn:hover {
    border-color: var(--tf-brand-primary, #119ae9);
}

.eap-grid-thumb {
    width: 100%;
    height: 72px;
    background: var(--tf-bg-muted, #f5f7fa);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    margin-bottom: 4px;
}

.eap-grid-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
}

.eap-grid-placeholder {
    font-size: 26px;
    font-weight: 600;
    color: var(--tf-text-muted, #6b7280);
}

.eap-grid-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--tf-text-primary, #133752);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
}

.eap-grid-type {
    font-size: 11px;
    font-weight: 500;
    color: var(--tf-text-muted, #6b7280);
}

.eap-grid-meta {
    font-size: 11px;
    color: var(--tf-text-muted, #6b7280);
}

/* ADR 0015 "All decorations" filter-lift toggle. */
.eap-all-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: var(--tf-text-muted, #6b7280);
    margin: 0 0 12px;
    cursor: pointer;
}

.eap-empty {
    font-size: 13px;
    color: var(--tf-text-muted, #6b7280);
    margin: 0;
    padding: 16px 0;
    text-align: center;
}

.eap-label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.75);
    font-weight: 500;
}

.eap-error {
    color: #d32f2f;
    font-size: 12px;
    margin: 0;
}

.eap-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 8px;
}

.eap-btn {
    padding: 6px 14px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: #fff;
    cursor: pointer;
    font-size: 14px;
}

.eap-btn--primary {
    background: var(--tf-brand-primary, #1976d2);
    color: #fff;
    border-color: var(--tf-brand-primary, #1976d2);
}

.eap-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
